import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth, getSignedInHintId } from "../../shared/auth";
import { isAdmin } from "../../shared/admin";
import { resolveDisplayName } from "../../shared/profile";
import { flagUrlSquare, flagName } from "../../shared/flags";
import { arenaLabel, arenaShapeOf, glyphBox, glyphFill } from "../setup/maps";
import Icon from "../setup/Icon";
import RankInsignia from "../../shared/rating/RankInsignia";
import { displayRating } from "../../shared/rating/rating";
import { TIERS, tierForValue, insigniaForValue, subProgress } from "../../shared/rating/tiers";
import { fetchRating, fetchMatchHistory, fetchAchievementHistory, fetchPlayerAchievements, fetchLeaderboardEntry, cachedRating, fetchRankedRatings, fetchRankedMatchHistory, fetchRankedLeaderboardEntry, RANKED_MODES, RANKED_PLACEMENT_GAMES, type LeaderboardEntry, type RatingRow, type MatchHistoryRow, type RankedMode, type RankedRatingRow, type RankedMatchHistoryRow, type AchievementHistoryRow, type PlayerAchievementsRow } from "../../shared/rating/store";
import { RANKED } from "../../shared/rating/ranked";
import { deriveMetrics, viewAchievements, progressFromHistory, ACHIEVEMENT_GROUPS } from "../../shared/rating/achievements";
import { LEVEL_NUMERALS } from "core/src/achievements/achievements.js";
import type { AchievementView } from "../../shared/rating/achievements";
import { levelColor } from "../play/match-end/UnlockCelebration";
import GoldenEggIcon from "../../shared/GoldenEggIcon";
import { formatDistancePx, formatDuration } from "../../shared/rating/stats-format";
import { OpenBanner, OpenableCard } from "./OpenAchievements";
import { useUnopenedAchievements } from "../../shared/achievements-open";
import { useEggBalance } from "../../shared/shop/useEggBalance";
import EggBalanceChip from "../../shared/shop/EggBalanceChip";
import SectionTabs from "../../shared/section-tabs/SectionTabs";
import MatchesTab, { boxTitle } from "./MatchesTab";
import RoyaleBlock, { timeAgo } from "./RoyaleBlock";
import { replayMayExist, replayPath } from "../../shared/replays";
import { useLandscapePhone } from "../../shared/useResponsive";
import { setReturnSheet, useLocalRoster } from "../lobby/start/hooks";
import PlayerSettings from "../lobby/start/PlayerSettings";
import { RankButtons } from "../lobby/start/parts";
import { useModalHeadSlot } from "../../shared/modal/Modal";
import styles from "./ProfilePage.module.scss";

// The profile page: OpenFront-style tabs over MS Battle-style content.
//   Overview     — identity card + stat tiles (core + fun derived stats)
//   Matches      — rating history graph (tier gridlines) + recent games
//   Achievements — the catalogue from achievements.ts with progress bars
// All data comes from casual_ratings + casual_match_results; nothing here
// writes anything.
//
// Two addresses: /profile is your own (sign-in required, with the account
// actions), /profile/<agentId> is any player's, reached from the leaderboard.
// Another player's Overview is public; their Matches and Achievements are
// only for themselves and admins. For a bot (whose ratings row and history
// are RLS-hidden and whose id the leaderboard masks) the rank card is built
// from the public leaderboard row instead, so it still reads as a player.

type Tab = "overview" | "matches" | "achievements";
// the phone rail's order, top to bottom
const RAIL_ORDER: Tab[] = ["overview", "achievements", "matches"];
const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "matches", label: "Matches" },
  { id: "achievements", label: "Achievements" },
];

const dr = (mu: number) => displayRating({ mu, sigma: 0 });

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return n + "th";
  return n + (["th", "st", "nd", "rd"][n % 10 < 4 ? n % 10 : 0] || "th");
}

// The most common value in a list (used for favorite arena / mode).
function modeOf(values: (string | null)[]): string | null {
  const counts = new Map<string, number>();
  for (const v of values) if (v) counts.set(v, (counts.get(v) || 0) + 1);
  let best: string | null = null, n = 0;
  for (const [v, c] of counts) if (c > n) { best = v; n = c; }
  return best;
}

// Stat box colours: the Record row is purple, the Habits row turquoise, and
// the Form row is green / red by meaning. The icon in its well, the value and
// the box's soft wash all take the same colour.
const PURPLE = "#8b7cf6";
const TURQUOISE = "#28c4d4";
const GOOD = "#46d97e";
const BAD = "#ff5c74";
const MUTED_BOX = "#8995b3"; // lifetime totals: no group colour, just the muted ink

// A stat box: an icon in a tinted well on the left, label over value on the
// right, the value in the box's colour. small: a text value (an arena name)
// rather than a number; unit: a muted word after the number ("days").
function Tile({ label, value, unit, hint, color, icon, small }: { label: string; value: React.ReactNode; unit?: string; hint?: string; color: string; icon: React.ReactNode; small?: boolean }) {
  return (
    <div className={styles.tile} title={hint} style={{ "--c": color } as React.CSSProperties}>
      <span className={styles.tileIcon} aria-hidden="true">{icon}</span>
      <span className={styles.tileLabel}>{label}</span>
      <span className={small ? `${styles.tileValue} ${styles.tileValueSmall}` : styles.tileValue}>
        {value}
        {unit ? <span className={styles.tileUnit}>{unit}</span> : null}
      </span>
    </div>
  );
}

// The favourite arena's icon is the arena itself: the same static thumbnail
// the setup screen's map tiles draw (shape from the map string, scaled by its
// real size so a Small Circle reads small and a Huge Square big; a rectangle at
// its own aspect, a stadium as a pill at its own aspect).
function ArenaGlyph({ map }: { map: string }) {
  const a = arenaShapeOf(map) || { shape: "square", size: 447, aspect: 1 };
  const fill = glyphFill(a.size);
  const s = 100 * fill, box = glyphBox(a.aspect, s);
  return (
    <svg viewBox="0 0 100 100" width="18" height="18" aria-hidden="true">
      {a.shape === "circle" ? (
        <circle cx="50" cy="50" r={s / 2} fill="none" stroke="currentColor" strokeWidth="9" />
      ) : (
        <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={a.shape === "stadium" ? Math.min(box.w, box.h) / 2 : 9} fill="none" stroke="currentColor" strokeWidth="9" />
      )}
    </svg>
  );
}

// MS Battle-style rating chart: line + soft area over tier-threshold gridlines.
// The line itself is tier-coloured: a hard-stop vertical gradient switches the
// stroke to each tier's metal exactly at its threshold, so a climb literally
// goes bronze → silver → gold as it rises.
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function RatingChart({ history, own }: { history: MatchHistoryRow[]; own: boolean }) {
  const gradId = "cg" + React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  // Which match is under the pointer (or focused): its dot grows and a small
  // card with the match's facts appears beside it.
  const [hover, setHover] = useState<number | null>(null);
  const W = 720, H = 240, PADX = 12, PADY = 16;
  const series = history.map((h) => dr(h.muAfter));
  if (series.length < 2) {
    return <div className={styles.chartEmpty}>Not enough matches for a graph yet</div>;
  }
  let lo = Math.min(...series), hi = Math.max(...series);
  const pad = Math.max(8, (hi - lo) * 0.15);
  lo -= pad; hi += pad;
  const x = (i: number) => PADX + (i / (series.length - 1)) * (W - PADX * 2);
  const y = (v: number) => PADY + (1 - (v - lo) / (hi - lo)) * (H - PADY * 2);
  const pts = series.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  // Gridlines: every tier boundary that falls inside the view.
  const bounds = TIERS.filter((t) => t.min >= lo && t.min <= hi).map((t) => ({ v: t.min, label: t.name, color: t.color }));
  // Tier-band gradient: bottom-to-top over the value range, with a hard colour
  // switch (two stops at the same offset) at every visible tier boundary.
  const f = (v: number) => (v - lo) / (hi - lo);
  const stops: { off: number; color: string }[] = [{ off: 0, color: tierForValue(lo).color }];
  for (const b of bounds) {
    stops.push({ off: f(b.v), color: tierForValue(b.v - 1).color });
    stops.push({ off: f(b.v), color: b.color });
  }
  stops.push({ off: 1, color: tierForValue(hi).color });
  return (
    <div className={styles.chartWrap}>
      <svg viewBox={`0 0 ${W} ${H}`} className={styles.chart} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1="0" y1={y(lo)} x2="0" y2={y(hi)}>
            {stops.map((st, i) => (
              <stop key={i} offset={st.off.toFixed(4)} stopColor={st.color} />
            ))}
          </linearGradient>
        </defs>
        {/* Tier thresholds as thin lines in the tier's colour; the colour
            says which tier, so no label. */}
        {bounds.map((b) => (
          <line key={b.v} x1={0} x2={W} y1={y(b.v)} y2={y(b.v)} stroke={b.color} strokeOpacity="0.35" strokeWidth="1" />
        ))}
        <polygon points={`${PADX},${H - PADY} ${pts.join(" ")} ${W - PADX},${H - PADY}`} fill={`url(#${gradId})`} opacity="0.14" />
        <polyline points={pts.join(" ")} fill="none" stroke={`url(#${gradId})`} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      {/* One dot per match, laid over the (stretched) SVG as HTML so the dots
          stay round at any width. The hover card flips to the left half way
          across so it never leaves the chart. */}
      <div className={styles.chartDots}>
        {history.map((h, i) => {
          const left = (x(i) / W) * 100, top = (y(series[i]) / H) * 100;
          const delta = dr(h.muAfter) - dr(h.muBefore);
          const on = hover === i;
          const place = h.placement === 1 ? "Won" : ordinal(h.placement);
          const label = `${shortDate(h.createdAt)}: ${place} of ${h.fieldSize}${h.map ? ", " + arenaLabel(h.map) : ""}, ${delta >= 0 ? "+" : ""}${delta}`;
          return (
            <React.Fragment key={i}>
              <button
                type="button"
                className={on ? `${styles.chartDot} ${styles.chartDotOn}` : styles.chartDot}
                style={{ left: `${left}%`, top: `${top}%`, "--dot": tierForValue(series[i]).color } as React.CSSProperties}
                aria-label={label}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((v) => (v === i ? null : v))}
                onFocus={() => setHover(i)}
                onBlur={() => setHover((v) => (v === i ? null : v))}
              />
              {on ? (
                <div
                  className={left > 55 ? `${styles.chartTip} ${styles.chartTipLeft}` : styles.chartTip}
                  style={{ left: `${left}%`, top: `${top}%` }}
                  role="tooltip"
                >
                  <span className={styles.chartTipDate}>{shortDate(h.createdAt)}</span>
                  <span className={`${styles.chartTipPlace} ${h.placement === 1 ? styles.toneWin : delta >= 0 ? styles.up : styles.down}`}>{place} of {h.fieldSize}</span>
                  {h.map ? <span className={styles.chartTipMap}>{arenaLabel(h.map)}{h.playstyle ? ` · ${h.playstyle}` : ""}</span> : null}
                  <span className={`${styles.chartTipDelta} ${h.placement === 1 ? styles.toneWin : delta >= 0 ? styles.up : styles.down}`}>{delta >= 0 ? "+" + delta : delta}</span>
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// A shimmering stand-in for a piece of the page that has not loaded yet: a
// bar of the given size (a pill when short, a circle when `round`). The
// skeletons sit inside the real containers (tile, match row, achievement
// card), so the page keeps its exact layout while it loads.
// An achievement's levels as a row of small hexagons: a reached one is filled in
// its level's colour with a tick, the others are outlines.
export function LevelHexes({ levels, level }: { levels: number; level: number }) {
  if (levels <= 1) return null;
  return (
    <span className={styles.lvHexes} aria-label={`Level ${level} of ${levels}`}>
      {Array.from({ length: levels }, (_, k) => (
        <svg key={k} viewBox="0 0 20 22" className={k < level ? styles.lvOn : undefined} style={{ "--pip": levelColor(k + 1) } as React.CSSProperties} aria-hidden="true">
          <polygon points="10,1 18.5,6 18.5,16 10,21 1.5,16 1.5,6" />
          {k < level ? <path d="M6 11.2l2.8 2.8 5.2-5.6" /> : null}
        </svg>
      ))}
    </span>
  );
}
// The phone's achievements: a true hexagon grid. Every hexagon is regular and
// they sit the same distance apart in every direction (the rows interlock, the
// odd ones half a step in). How many fit in a row follows the room there is.
// A hexagon tells its level with its own lower point: that corner is filled in
// the level's colour and carries the numeral, and the outline takes the same
// colour, so the level is part of the shape and nothing is pinned onto it.
const HEX_W = 58, HEX_GAP = 7;
const HEX_H = (HEX_W * 2) / Math.sqrt(3);            // a regular hexagon, point up
const HEX_STEP_X = HEX_W + HEX_GAP;
const HEX_STEP_Y = HEX_H * 0.75 + HEX_GAP * (Math.sqrt(3) / 2);
const HEX_PTS = "50,2 98,29.87 98,85.6 50,113.47 2,85.6 2,29.87"; // in a 100 x 115.47 box, 2 in from the edge for the stroke
function Hive({ items, selected, loading, onPick }: { items: AchievementView[]; selected: string | null; loading: boolean; onPick: (id: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(6);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setCols(Math.max(3, Math.floor((el.clientWidth - HEX_STEP_X / 2 + HEX_GAP) / HEX_STEP_X)));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const rows = Math.ceil(items.length / cols);
  return (
    <div ref={ref} className={styles.hive} style={{ height: (rows - 1) * HEX_STEP_Y + HEX_H }}>
      {items.map((a, i) => {
        const row = Math.floor(i / cols), col = i % cols;
        const reached = !loading && a.level > 0;
        return (
          <button
            key={a.id}
            type="button"
            className={[styles.hexT, reached ? styles.hexDone : styles.hexLocked, selected === a.id ? styles.hexSel : ""].filter(Boolean).join(" ")}
            style={{ left: col * HEX_STEP_X + (row % 2 ? HEX_STEP_X / 2 : 0), top: row * HEX_STEP_Y, width: HEX_W, height: HEX_H }}
            aria-label={a.levels > 1 ? `${a.name}, level ${a.level} of ${a.levels}` : a.name}
            aria-pressed={selected === a.id}
            onClick={() => onPick(a.id)}
          >
            <svg viewBox="0 0 100 115.47" aria-hidden="true" style={reached ? ({ "--lv": levelColor(a.levels > 1 ? a.level : 3) } as React.CSSProperties) : undefined}>
              <defs><clipPath id={`hx-${a.id}`}><polygon points={HEX_PTS} /></clipPath></defs>
              <polygon className={styles.hexFill} points={HEX_PTS} />
              {/* the level: the hexagon's lower point, filled, with the numeral in it */}
              {reached && a.levels > 1 ? (
                <>
                  <rect className={styles.hexFoot} x="0" y="80" width="100" height="36" clipPath={`url(#hx-${a.id})`} />
                  <text className={styles.hexNum} x="50" y="97.5" textAnchor="middle">{LEVEL_NUMERALS[a.level - 1]}</text>
                </>
              ) : null}
              <polygon className={styles.hexEdge} points={HEX_PTS} />
            </svg>
            <em className={reached && a.levels > 1 ? styles.hexUp : undefined}>{a.icon}</em>
          </button>
        );
      })}
    </div>
  );
}

// The way to the next level: a square bar, the count under it, and the eggs it pays.
export function AchProgress({ a }: { a: AchievementView }) {
  const pct = a.to == null ? 100 : Math.round((100 * Math.max(0, a.value - a.from)) / Math.max(1, a.to - a.from));
  return (
    <>
      {!a.done ? <div className={styles.achBar}><div className={styles.achFill} style={{ width: pct + "%" }} /></div> : null}
      <span className={styles.achProgress}>
        <span>{a.done ? "Complete" : a.to != null && a.to > 1 ? `${a.value.toLocaleString("en-US")} / ${a.to.toLocaleString("en-US")}` : "Not yet"}</span>
        {a.nextEggs != null ? <span className={styles.achReward}>+{a.nextEggs} <GoldenEggIcon size={13} /></span> : null}
      </span>
    </>
  );
}

// A goal as a sentence: a full stop, unless it already ends in one ("What goes up...").
const sentence = (t: string) => (/[.!?]$/.test(t) ? t : t + ".");

function Skel({ w, h = 10, round, className, style }: { w?: number | string; h?: number; round?: boolean; className?: string; style?: React.CSSProperties }) {
  return (
    <span
      className={className ? `${styles.skel} ${className}` : styles.skel}
      style={{ width: w, height: h, borderRadius: round ? "50%" : h <= 16 ? 999 : 8, ...style }}
      aria-hidden="true"
    />
  );
}
function SkelTile() {
  return (
    <div className={styles.tile}>
      <span className={styles.tileIcon}><Skel w={18} h={18} round /></span>
      <span className={styles.tileLabel}><Skel w={64} h={8} /></span>
      <span className={styles.tileValue}><Skel w={48} h={18} /></span>
    </div>
  );
}

// A rank card's worth of RatingRow from a public leaderboard row, for a
// player whose ratings row can't be read (a bot, or the RLS-hidden case).
// mu is back-solved from the display rating so the tier math agrees with
// the leaderboard's number; peak is unknown, so it reads as the current rating.
function rowFromLeaderboard(e: LeaderboardEntry): RatingRow {
  return {
    agentId: e.agentId,
    displayName: e.displayName,
    rating: { mu: muForDisplay(e.rating), sigma: 0 },
    displayRating: e.rating,
    games: e.games,
    wins: e.wins,
    podiums: e.podiums,
    bestPlace: e.bestPlace,
    peak: e.rating,
    placementPct: e.placementPct,
    lifetimeStats: null,
  };
}

// A ranked card from a ranked ladder row (someone whose ratings row is not
// readable): the ladder only lists players past their placements.
function rankedFromLadder(mode: RankedMode, e: LeaderboardEntry | null | undefined): RankedRatingRow | null {
  return e ? { ...rowFromLeaderboard(e), mode, placed: true } : null;
}
// The mu whose display rating is `value` (displayRating is monotonic in mu;
// bisect over its range). The invariant keeps dr(hi) >= value, so hi is the
// answer: the midpoint could round to one step below. Only used for the
// read-only rank card above.
function muForDisplay(value: number): number {
  let lo = -50, hi = 150;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (dr(mid) < value) lo = mid; else hi = mid;
  }
  return hi;
}

export default function ProfilePage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  // your player: what you wear (colour included) is changed here as in the navbar's menu
  const roster = useLocalRoster();
  // A landscape phone gets its own one-screen layout (two columns, no tabs);
  // see the `app` branch at the end.
  const app = useLandscapePhone();
  // ...where one achievement at a time is spelled out under the hexagons.
  const [achSel, setAchSel] = useState<string | null>(null);
  // /profile/<agentId>: someone else's profile (their own address for the
  // signed-in player just reads as /profile).
  const { agentId: paramId } = useParams<{ agentId: string }>();
  const other = !!paramId && paramId !== user?.id;
  // The private tabs (Matches, Achievements) belong to the player and admins.
  const privateTabs = !other || isAdmin(user);
  // Achievements waiting to be opened: a count and a glow on their tab (your own profile).
  const unopened = useUnopenedAchievements();
  const eggs = useEggBalance();
  const tabs = (privateTabs ? TABS : TABS.filter((t) => t.id === "overview")).map((t) => (t.id === "achievements" && !other && unopened.length ? { ...t, badge: unopened.length } : t));
  // The tab lives in the URL (?tab=matches; the default overview keeps it
  // clean), like the shop's departments, so each tab is its own address and a
  // reload lands back on it.
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t = params.get("tab");
    return (TABS.some((x) => x.id === t) ? t : "overview") as Tab;
  });
  // The tab list can shrink (signing out while on an admin-only tab of
  // someone else's profile): the selection falls back to the overview.
  const shownTab: Tab = tabs.some((t) => t.id === tab) ? tab : "overview";
  const selectTab = (t: Tab) => {
    setTab(t);
    setParams(t === "overview" ? {} : { tab: t }, { replace: true });
  };
  const [row, setRow] = useState<RatingRow | null | undefined>(undefined);
  const [history, setHistory] = useState<MatchHistoryRow[] | null | undefined>(undefined);
  // The player's public leaderboard row: their position, and the rank card's
  // fallback source when the ratings row is unreadable.
  const [entry, setEntry] = useState<LeaderboardEntry | null | undefined>(undefined);
  // The ranked ladders: both rows (null = never played that mode), their
  // history, and where the player sits on each ladder.
  const [ranked, setRanked] = useState<Record<RankedMode, RankedRatingRow | null> | null | undefined>(undefined);
  const [rankedHistory, setRankedHistory] = useState<RankedMatchHistoryRow[] | null | undefined>(undefined);
  const [rankedPos, setRankedPos] = useState<Record<RankedMode, number | null>>({ duel: null, ffa: null });
  // Their rows on the ranked ladders (undefined: loading): where they sit, and
  // the ranked cards' source when the ratings rows are unreadable, as `entry`
  // is for the casual card.
  const [rankedEntries, setRankedEntries] = useState<Partial<Record<RankedMode, LeaderboardEntry | null>>>({});
  // Every match on record, for the achievements (they are for keeps, so not
  // just the latest page the Matches tab shows). Loaded with that tab.
  const [achHistory, setAchHistory] = useState<{ id: string; rows: AchievementHistoryRow[] | null } | undefined>(undefined);
  // The server's own record of their achievements (levels for keeps); absent
  // until they play a rated match after achievements launched.
  const [achRecord, setAchRecord] = useState<{ id: string; row: PlayerAchievementsRow | null | undefined } | undefined>(undefined);
  // Which ladder the graph draws.
  const [chartMode, setChartMode] = useState<RankedMode | "casual">("casual");

  // Whose profile this is: the URL's player, else the signed-in player.
  const subjectId = other ? paramId! : user?.id ?? null;
  useEffect(() => {
    if (!subjectId) {
      // Still checking the session: leave everything unknown (undefined), so
      // the page shows skeletons, not the signed-out empty states.
      if (!authLoading) { setRow(null); setHistory(null); setEntry(null); setRanked(null); setRankedHistory(null); }
      return;
    }
    setRow(undefined); setHistory(undefined); setEntry(undefined); setRanked(undefined); setRankedHistory(undefined);
    let live = true;
    fetchRating(subjectId).then((r) => live && setRow(r));
    fetchMatchHistory(subjectId).then((h) => live && setHistory(h));
    fetchLeaderboardEntry(subjectId).then((e) => live && setEntry(e));
    fetchRankedRatings(subjectId).then((r) => live && setRanked(r ?? { duel: null, ffa: null }));
    fetchRankedMatchHistory(subjectId).then((h) => live && setRankedHistory(h ?? []));
    setRankedEntries({});
    for (const m of RANKED_MODES) fetchRankedLeaderboardEntry(m, subjectId).then((e) => {
      if (!live) return;
      setRankedPos((cur) => ({ ...cur, [m]: e ? e.position : null }));
      setRankedEntries((cur) => ({ ...cur, [m]: e }));
    });
    return () => { live = false; };
  }, [subjectId, authLoading]);
  const position = entry?.position ?? null;
  const wantAch = shownTab === "achievements" && !!subjectId;
  useEffect(() => {
    if (!wantAch || achHistory?.id === subjectId) return;
    let live = true;
    const id = subjectId!;
    fetchAchievementHistory(id).then((rows) => live && setAchHistory({ id, rows }));
    fetchPlayerAchievements(id).then((row) => live && setAchRecord({ id, row }));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantAch, subjectId]);

  // A reload while signed in: the session check is still running (no `user`
  // yet) but the last visit was signed in, so this is a loading profile, not
  // a signed-out one. The rank card seeds from that account's cached rating
  // (as the lobby's badge does); everything else shows a skeleton until the
  // data lands, never the empty "no matches yet" states.
  const hintId = !other && !user && authLoading ? getSignedInHintId() : null;
  const pending = !other && !user && !!hintId;
  const accountId = subjectId ?? hintId;
  const cached = accountId ? cachedRating(accountId) : null;
  // Someone else's profile with no readable ratings row: once the leaderboard
  // row is in, the rank card is built from that (null there too = unknown
  // player). Until both have answered, the card keeps loading.
  let shownRow: RatingRow | null | undefined = row === undefined ? cached ?? undefined : row; // undefined: not known yet
  if (other && row === null) shownRow = entry === undefined ? undefined : entry ? rowFromLeaderboard(entry) : null;
  const loadingRank = shownRow === undefined;
  const loadingHistory = pending || history === undefined;
  // Another player with nothing on record anywhere (no casual row or ladder
  // place, on neither ranked ladder): not a player we know.
  const rankedLoaded = RANKED_MODES.every((m) => rankedEntries[m] !== undefined);
  const unknown = other && row === null && entry === null && rankedLoaded && RANKED_MODES.every((m) => !rankedEntries[m]);
  // Their ranked cards: the ratings rows, else built from the ladder rows.
  const rankedShown: Record<RankedMode, RankedRatingRow | null> | null | undefined = ranked && other
    ? { duel: ranked.duel ?? rankedFromLadder("duel", rankedEntries.duel), ffa: ranked.ffa ?? rankedFromLadder("ffa", rankedEntries.ffa) }
    : ranked;

  // undefined: loading. Nobody to look up (signed out): nothing on record, not loading.
  const achRows = !subjectId ? (authLoading ? undefined : null) : achHistory && achHistory.id === subjectId ? achHistory.rows : undefined;
  const achRow = !subjectId ? null : achRecord && achRecord.id === subjectId ? achRecord.row ?? null : undefined;
  const loadingAch = loadingHistory || achRows === undefined || achRow === undefined;
  const metrics = useMemo(() => deriveMetrics((shownRow ?? row) ?? null, rankedShown ?? null, achRows ?? null), [shownRow, row, rankedShown, achRows]);
  const achievements = useMemo(
    () => (achRow ? viewAchievements(achRow.progress, achRow.levels) : viewAchievements(progressFromHistory(achRows ?? []), null)),
    [achRow, achRows],
  );
  const levelsReached = achievements.reduce((n, a) => n + a.level, 0);
  const levelsTotal = achievements.reduce((n, a) => n + a.levels, 0);

  // A landscape phone, your own profile: the way to Player Settings sits in the
  // top bar, left of the eggs (as Profile does on that page). It opens the home
  // screen with Player Settings up.
  // (PC: your egg count, where the shop has it; opened achievements fly their
  // eggs into it)
  useModalHeadSlot(!app ? (!other && user && eggs != null ? <EggBalanceChip value={eggs} /> : null) : !other && user ? (
    <div className={styles.appHeadBtns}>
      {/* as on Player Settings: Sign out, quiet with a red edge, left of the other */}
      <button type="button" className={styles.appOut} onClick={() => signOut()}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></svg>
        Sign out
      </button>
      <button type="button" className={styles.appSettings} onClick={() => { setReturnSheet("player"); navigate("/"); }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" /></svg>
        Player settings
      </button>
    </div>
  ) : null);

  if (!other && !user && !pending) {
    return (
      <div className="section">
        {/* Signed out, the profile is one button: the way to get one. */}
        <div className={styles.signedOut}>
          <svg className={styles.signedOutBadge} viewBox="0 0 64 64" aria-hidden="true">
            <path d="M32 4l24.25 14v28L32 60 7.75 46V18z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.16)" strokeWidth="2" />
            <path d="M32 12l17.3 10v20L32 52 14.7 42V22z" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
            <rect x="24" y="30" width="16" height="12" rx="2" fill="none" stroke="#8ea3bd" strokeWidth="2.4" />
            <path d="M27 30v-4a5 5 0 0 1 10 0v4" fill="none" stroke="#8ea3bd" strokeWidth="2.4" />
          </svg>
          <button type="button" className={styles.signedOutBtn} onClick={() => navigate("/login")}>Sign in to see your profile</button>
        </div>
      </div>
    );
  }
  if (unknown) {
    return (
      <div className="section">
        <div className="section-placeholder">No player by that name here. They may not have played an online match yet.</div>
      </div>
    );
  }

  // Another player's name comes from their rating row (the leaderboard's
  // name); your own is your account's. Flag and join date are only known
  // for your own account.
  // (your own: the name you play under, as set in the navbar's menu)
  const name = other ? shownRow?.displayName ?? rankedEntries.duel?.displayName ?? rankedEntries.ffa?.displayName ?? "" : user ? (roster.players[0]?.name || resolveDisplayName(user)) : "";
  const flag = other ? undefined : roster.players[0]?.flag;
  const memberSince = !other && user?.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : null;
  const hist = history ?? [];
  const favoriteMap = modeOf(hist.map((h) => h.map));
  const winRate = metrics.games ? Math.round((100 * metrics.wins) / metrics.games) : null;
  const podiumRate = metrics.games ? Math.round((100 * metrics.podiums) / metrics.games) : null;
  const ratingNow = shownRow ? displayRating(shownRow.rating) : null;
  // Same helper as the match-end "Your rating" box, so the two agree.
  const progress = ratingNow != null ? subProgress(ratingNow) : null;
  const insignia = ratingNow != null ? insigniaForValue(ratingNow) : null;
  const last = hist.length ? hist[hist.length - 1] : null;
  const lastDelta = last ? dr(last.muAfter) - dr(last.muBefore) : 0;
  const tierColor = insignia ? insignia.tier.color : null;
  const avgField = hist.length ? (hist.reduce((s, h) => s + h.fieldSize, 0) / hist.length).toFixed(1) : null;
  const lifetime = shownRow?.lifetimeStats ?? null;

  // The record, form and habits boxes (shared by both layouts).
  const statRows = loadingHistory ? (
    <div className={styles.rows}>
      {[0, 1, 2].map((r) => (
        <div key={r} className={styles.row4}>
          {[0, 1, 2, 3].map((c) => <SkelTile key={c} />)}
        </div>
      ))}
    </div>
  ) : (
    <div className={styles.rows}>
      <div className={styles.row4}>
        <Tile label="Games" value={metrics.games} color={PURPLE} icon={<Icon name="worm" size={18} />} />
        <Tile label="Wins" value={metrics.wins} color={PURPLE} icon={<Icon name="trophy" size={18} />} />
        <Tile label="Podiums" value={metrics.podiums} color={PURPLE} icon={<Icon name="podium" size={18} />} />
        <Tile label="Podium rate" value={podiumRate == null ? "–" : podiumRate + "%"} color={PURPLE} icon={<Icon name="cup" size={18} />} hint="Share of your online matches that ended top 3." />
      </div>
      <div className={styles.row4}>
        <Tile label="Win rate" value={winRate == null ? "–" : winRate + "%"} color={GOOD} icon={<Icon name="target" size={18} />} hint="Share of your online matches you won outright." />
        <Tile label="Place %" value={metrics.placementPct == null ? "–" : metrics.placementPct + "%"} color={GOOD} icon={<Icon name="gauge" size={18} />} hint="How much of the field you typically beat: 100% = you win every game, 50% = mid-field." />
        <Tile label="Best climb" value={metrics.biggestClimb ? "+" + metrics.biggestClimb : "–"} color={GOOD} icon={<Icon name="trend-up" size={18} />} />
        <Tile label="Worst tumble" value={metrics.biggestDrop ? "−" + metrics.biggestDrop : "–"} color={BAD} icon={<Icon name="trend-down" size={18} />} />
      </div>
      <div className={styles.row4}>
        <Tile label="Favorite arena" value={favoriteMap ? arenaLabel(favoriteMap) : "–"} color={TURQUOISE} icon={favoriteMap ? <ArenaGlyph map={favoriteMap} /> : <Icon name="map" size={18} />} small />
        <Tile label="Field size" value={avgField ? Math.round(Number(avgField)) : "–"} unit={avgField ? "worms avg" : undefined} color={TURQUOISE} icon={<Icon name="users" size={18} />} />
        <Tile label="Longest streak" value={metrics.longestStreakDays || "–"} unit={metrics.longestStreakDays ? (metrics.longestStreakDays === 1 ? "day" : "days") : undefined} color={TURQUOISE} icon={<Icon name="flame" size={18} />} hint="Consecutive days with at least one online match." />
        <Tile label="Busiest day" value={metrics.maxGamesInDay || "–"} unit={metrics.maxGamesInDay ? (metrics.maxGamesInDay === 1 ? "game" : "games") : undefined} color={TURQUOISE} icon={<Icon name="calendar" size={18} />} />
      </div>
    </div>
    );


  // ---------- a landscape phone: a rail down the left, one page beside it ----------
  // The rail: who this is, then Overview, Achievements and Matches (the same
  // tabs as the desktop page, with the same addresses). Overview: the two
  // ranked ladders, the Royale and the stat boxes. Someone else's profile has
  // only the overview, so no rail. (What you wear is on Player Settings.)
  if (app) {
    const sel = achievements.find((a) => a.id === achSel) ?? achievements.find((a) => !a.done) ?? achievements[0];
    const recent = [...(history ?? []), ...(rankedHistory ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 30);
    const loadingMatches = loadingHistory || rankedHistory === undefined;
    const ident = (
      <div className={styles.appWho}>
        {flag ? <img src={flagUrlSquare(flag)} alt={flagName(flag)} /> : null}
        <div>
          {pending || (other && loadingRank) ? <Skel w={90} h={14} /> : <b>{name}</b>}
          {memberSince ? <small>Since {memberSince}</small> : null}
        </div>
      </div>
    );
    return (
      <div className={`${styles.app} ${tabs.length > 1 ? "" : styles.appNoRail}`}>
        {tabs.length > 1 ? (
          <nav className={styles.appRail} aria-label="Profile">
            {ident}
            {/* (Achievements before Matches here; the desktop band keeps its own order) */}
            {RAIL_ORDER.map((id) => tabs.find((t) => t.id === id)).filter((t): t is { id: Tab; label: string } => !!t).map((t) => (
              <button key={t.id} type="button" className={t.id === shownTab ? styles.appTabOn : undefined} aria-current={t.id === shownTab ? "page" : undefined} onClick={() => selectTab(t.id)}>{t.label}</button>
            ))}
          </nav>
        ) : null}
        <div className={styles.appMain}>
          {shownTab === "overview" ? (
            <>
              {tabs.length > 1 ? null : ident}
              {/* the two ladders, as the start page shows them (the same component):
                  badge, tier, rating, and the way to the next tier or through
                  the placements. A tap opens that ladder's leaderboard. */}
              <RankButtons ratings={rankedShown} signedIn sideBySide onOpen={(m) => navigate("/leaderboard" + (m === "duel" ? "" : "?tab=" + m))} />
              {!other && user ? <RoyaleBlock /> : null}
              {statRows}
            </>
          ) : shownTab === "achievements" ? (
            <>
              <div className={styles.appH}><span>Achievements</span><small>{loadingHistory ? "" : `${achievements.filter((a) => a.level > 0).length} / ${achievements.length}`}</small></div>
              {!other && <OpenBanner unopened={unopened} />}
              <Hive items={achievements} selected={sel ? sel.id : null} loading={loadingHistory} onPick={setAchSel} />
              {sel ? (
                // the one you tapped, spelled out: its levels, what the next one asks, how far along, what it pays
                <div className={styles.achCap} style={{ "--lv": levelColor(sel.levels > 1 ? Math.max(1, sel.level) : 3) } as React.CSSProperties}>
                  <span className={styles.achTop}><b>{sel.name}</b><LevelHexes levels={sel.levels} level={loadingHistory ? 0 : sel.level} /></span>
                  <small>{(sel.done ? (sel.levels > 1 ? "Every level reached. " : "") : sel.level && sel.levels > 1 ? "Next: " : "") + sentence(sel.goal)}</small>
                  {loadingHistory ? null : <AchProgress a={sel} />}
                </div>
              ) : null}
            </>
          ) : (
            <>
              {loadingMatches ? (
                <div className={styles.appMatches}>{[0, 1, 2].map((i) => <div key={i} className={styles.appMatch}><i /><span><Skel w={i % 2 ? 120 : 90} h={11} /></span></div>)}</div>
              ) : recent.length === 0 ? (
                <div className={styles.appEmpty}>No online matches yet.</div>
              ) : (
                <div className={styles.appMatches}>
                  {recent.map((h, i) => {
                    const delta = dr(h.muAfter) - dr(h.muBefore);
                    const tone = h.placement === 1 ? styles.mWin : delta >= 0 ? styles.mUp : styles.mDown;
                    const inner = (
                      <>
                        <i />
                        <span>
                          <b>{boxTitle(h.matchId) || "Online match"}</b>
                          <small>{h.placement === 1 ? "Won" : ordinal(h.placement)} of {h.fieldSize} · {timeAgo(h.createdAt)}</small>
                        </span>
                        <strong>{delta >= 0 ? "+" + delta : "−" + Math.abs(delta)}</strong>
                      </>
                    );
                    return h.matchId && replayMayExist(h.createdAt)
                      ? <Link key={i} className={`${styles.appMatch} ${tone}`} to={replayPath(h.matchId, null)} title="Watch this match">{inner}<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></Link>
                      : <div key={i} className={`${styles.appMatch} ${tone}`}>{inner}</div>;
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`section ${styles.root}`}>
      {/* The tab band shared with the Shop. A public profile has only the
          Overview, so no band at all (a one-tab band is just a stray line). */}
      {tabs.length > 1 ? <SectionTabs<Tab> tabs={tabs} value={shownTab} onChange={selectTab} /> : null}

      {shownTab === "overview" && (
        <>
          {/* The identity row sits straight on the page, no card: the rank card
              and record tiles below are the boxes, and a box above them read
              as one too many. */}
          <div className={styles.identityRow}>
            <div className={styles.identity}>
              {flag ? <img className={styles.identityFlag} src={flagUrlSquare(flag)} alt={flagName(flag)} /> : null}
              <div className={styles.identityText}>
                {pending || (other && loadingRank) ? (
                  <>
                    <Skel w={150} h={18} style={{ marginBottom: 8 }} />
                    <Skel w={110} h={10} />
                  </>
                ) : (
                  <>
                    <span className={styles.identityName}>{name}</span>
                    {memberSince ? <span className={styles.identitySince}>Member since {memberSince}</span> : null}
                  </>
                )}
              </div>
              {/* Account actions share the row with the name (this page replaced
                  the old avatar modal); they wrap underneath on narrow screens.
                  Only on your own profile. */}
              {!other ? (
                <div className={styles.accountActions}>
                  {isAdmin(user) && (
                    <button className="app-btn app-btn-ghost" onClick={() => navigate("/admin")}>Admin tools</button>
                  )}
                  <button className={`app-btn app-btn-ghost ${styles.logout}`} onClick={() => signOut()}>Log out</button>
                </div>
              ) : null}
            </div>
          </div>

          {/* What you wear (your own profile only: it lives on this device, and
              syncs to your account): the colour (a click opens the colours) and
              the Store's cards, as in the navbar's menu; a card leads to its tab. */}
          {!other ? (
            <div className={styles.equipRow}>
              <div className={styles.rankedCaption}>Equipped</div>
              <PlayerSettings roster={roster} size="lg" fields={false} onShop={(tab) => navigate(tab ? "/shop?tab=" + tab : "/shop")} />
            </div>
          ) : null}

          {/* The ranked ladders first: one card per mode, the same shape as the
              casual card below. Placements show the egg and how far along. */}
          <div className={styles.rankedRow}>
            {RANKED_MODES.map((m) => {
              const r = rankedShown ? rankedShown[m] : null;
              const placed = !!r && r.placed;
              const v = placed ? r!.displayRating : null;
              const ins = v != null ? insigniaForValue(v) : null;
              const pr = v != null ? subProgress(v) : null;
              const played = r ? Math.min(r.games, RANKED_PLACEMENT_GAMES) : 0;
              const hist = (rankedHistory ?? []).filter((h) => h.mode === m);
              const lastR = hist.length ? hist[hist.length - 1] : null;
              const lastRDelta = lastR ? dr(lastR.muAfter) - dr(lastR.muBefore) : 0;
              return (
                <div key={m} className={styles.rank} style={ins ? ({ "--tier": ins.tier.color } as React.CSSProperties) : undefined}>
                  <div className={styles.rankedCaption}>Ranked · {RANKED[m].title}</div>
                  {rankedShown === undefined ? (
                    <div className={styles.rankTop}>
                      <Skel w={56} h={56} round />
                      <div><Skel w={90} h={10} style={{ marginBottom: 10 }} /><Skel w={70} h={22} /></div>
                    </div>
                  ) : v != null && ins ? (
                    <>
                      <div className={styles.rankTop}>
                        <RankInsignia value={v} size={56} />
                        <div>
                          <div className={styles.rankTier}>{ins.label}</div>
                          <div className={styles.rankRating}>{v}</div>
                        </div>
                      </div>
                      <div>
                        <div className={styles.rankBar} role="progressbar" aria-valuemin={0} aria-valuemax={1} aria-valuenow={pr ? Math.round(pr.pct * 100) / 100 : 1}>
                          <i style={{ width: Math.round(Math.max(0, Math.min(1, pr ? pr.pct : 1)) * 100) + "%" }} />
                        </div>
                        <div className={styles.rankCap}>
                          <span className={styles.rankCapTier}>{ins.label}</span>
                          <span className={styles.rankCapProgress}>{pr ? `${pr.toNext} left` : "MAX"}</span>
                        </div>
                      </div>
                      <div className={styles.rankFacts}>
                        <div className={styles.rankMeta}>
                          <span>Peak</span>
                          <span className={styles.rankPeak}><RankInsignia value={r!.peak || v} size={16} /><b>{r!.peak || v}</b></span>
                        </div>
                        <div className={styles.rankMeta}>
                          <span>Leaderboard</span>
                          {rankedPos[m]
                            ? <button type="button" className={styles.rankLink} onClick={() => navigate("/leaderboard" + (m === "duel" ? "" : "?tab=" + m))} title="See yourself on the leaderboard"><b>#{rankedPos[m]}</b></button>
                            : <b>–</b>}
                        </div>
                        <div className={styles.rankMeta}>
                          <span>Last match</span>
                          {lastR
                            ? <b className={lastR.placement === 1 ? styles.toneWin : lastRDelta >= 0 ? styles.up : styles.down}>{lastRDelta >= 0 ? "+" + lastRDelta : lastRDelta}</b>
                            : <b>–</b>}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={styles.rankTop}>
                      <RankInsignia rating={null} placeholder size={44} placeholderTitle="Placements" />
                      <div>
                        <div className={styles.rankTier}>{played ? `Placements ${played} of ${RANKED_PLACEMENT_GAMES}` : "Unranked"}</div>
                        <div className={styles.rankHint}>{played ? `${RANKED_PLACEMENT_GAMES - played} more and your rank hatches.` : `Play ${RANKED_PLACEMENT_GAMES} ranked matches to hatch your rank.`}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* The Royale: wins, the win rewards to open, recent Royales. Yours only
              (it reads the signed-in player's own tally). */}
          {!other && user ? <RoyaleBlock /> : null}

          {/* Rank card (where you stand and how far the next step is) beside a
              compact 3x2 of the record; the habits follow as one line of chips. */}
          <div className={styles.rankRow}>
            <div className={styles.rank} style={tierColor ? ({ "--tier": tierColor } as React.CSSProperties) : undefined}>
              <div className={styles.rankedCaption}>Casual</div>
              {ratingNow != null && insignia ? (
                <>
                  {/* The top half mirrors the match-end "Your rating" box: badge,
                      tier and number, the square bar with a bright tip, and the
                      "Gold I / 37 left" caption. The last match's delta stays
                      in the facts below, not in the corner. */}
                  <div className={styles.rankTop}>
                    <RankInsignia value={ratingNow} size={56} />
                    <div>
                      <div className={styles.rankTier}>{insignia.label}</div>
                      <div className={styles.rankRating}>{ratingNow}</div>
                    </div>
                  </div>
                  <div>
                    <div className={styles.rankBar} role="progressbar" aria-valuemin={0} aria-valuemax={1} aria-valuenow={progress ? Math.round(progress.pct * 100) / 100 : 1} aria-label={progress ? `Progress to ${progress.nextLabel}` : "Top of the ladder"}>
                      <i style={{ width: Math.round(Math.max(0, Math.min(1, progress ? progress.pct : 1)) * 100) + "%" }} />
                    </div>
                    <div className={styles.rankCap}>
                      <span className={styles.rankCapTier}>{insignia.label}</span>
                      <span className={styles.rankCapProgress}>{progress ? `${progress.toNext} left` : "MAX"}</span>
                    </div>
                  </div>
                  <div className={styles.rankFacts}>
                    <div className={styles.rankMeta}>
                      <span>Peak</span>
                      <span className={styles.rankPeak}>
                        <RankInsignia value={metrics.peak || ratingNow} size={16} />
                        <b>{metrics.peak || ratingNow}</b>
                      </span>
                    </div>
                    <div className={styles.rankMeta}>
                      <span>Leaderboard</span>
                      {position ? (
                        <button type="button" className={styles.rankLink} onClick={() => navigate("/leaderboard")} title={other ? "Open the leaderboard" : "See yourself on the leaderboard"}>
                          <b>#{position}</b>
                        </button>
                      ) : (
                        <b>–</b>
                      )}
                    </div>
                    <div className={styles.rankMeta}>
                      <span>Last match</span>
                      {last ? (
                        <b className={last.placement === 1 ? styles.toneWin : lastDelta >= 0 ? styles.up : styles.down} title={`${last.placement === 1 ? "Won" : ordinal(last.placement)} of ${last.fieldSize}, ${shortDate(last.createdAt)}`}>{lastDelta >= 0 ? "+" + lastDelta : lastDelta}</b>
                      ) : (
                        <b>–</b>
                      )}
                    </div>
                  </div>
                </>
              ) : loadingRank ? (
                <div className={styles.rankTop}>
                  <Skel w={56} h={56} round />
                  <div>
                    <Skel w={90} h={10} style={{ marginBottom: 10 }} />
                    <Skel w={70} h={22} />
                  </div>
                </div>
              ) : (
                <div className={styles.rankTop}>
                  <RankInsignia rating={null} placeholder size={44} />
                  <div>
                    <div className={styles.rankTier}>Unranked</div>
                    <div className={styles.rankHint}>{other ? "No online match played yet." : "Play an online match to hatch your rank."}</div>
                  </div>
                </div>
              )}
            </div>
            {/* The rating graph beside the rank card: where you are, and how
                you got there. */}
            <div className={styles.chartCard}>
              <div className={styles.chartModes}>
                {([["casual", "Casual"], ["duel", "1 vs 1"], ["ffa", "6 FFA"]] as [RankedMode | "casual", string][]).map(([id, label]) => (
                  <button key={id} type="button" className={`${styles.chartMode} ${chartMode === id ? styles.chartModeOn : ""}`} onClick={() => setChartMode(id)}>{label}</button>
                ))}
              </div>
              {loadingHistory || (chartMode !== "casual" && rankedHistory === undefined) ? (
                <Skel className={styles.skelChart} />
              ) : (
                <RatingChart history={chartMode === "casual" ? hist : (rankedHistory ?? []).filter((h) => h.mode === chartMode)} own={!other} />
              )}
            </div>
          </div>

          {/* Three rows of four, grouped: the record (purple), form (green and
              red by meaning), habits (turquoise). Each box carries an icon. */}
          {statRows}

          {/* Lifetime worming: real accumulated totals from the server
              (casual_ratings.lifetime_stats). Absent until the first
              stat-tracked match, so the section simply doesn't render before
              then. */}
          {lifetime ? (
            <>
              <h3 className={styles.h}>Lifetime worming</h3>
              <div className={styles.tiles}>
                <Tile label="Distance wormed" value={formatDistancePx(lifetime.distance || 0)} color={MUTED_BOX} icon={<Icon name="worm" size={18} />} />
                <Tile label="Power-ups grabbed" value={(lifetime.powerUps?.self || 0) + (lifetime.powerUps?.others || 0) + (lifetime.powerUps?.all || 0)} color={MUTED_BOX} icon={<Icon name="bolt" size={18} />} />
                <Tile label="Crashes caused" value={lifetime.crashesCaused || 0} color={MUTED_BOX} icon={<Icon name="target" size={18} />} hint="Opponents who ended their round on your trail." />
                <Tile label="Gaps threaded" value={lifetime.gapsThreaded || 0} color={MUTED_BOX} icon={<Icon name="sliders" size={18} />} hint="Squeezes through with trail or wall on both sides." />
                <Tile label="Rounds won" value={(lifetime.roundsWon || 0) + (lifetime.rounds ? " of " + lifetime.rounds : "")} color={MUTED_BOX} icon={<Icon name="trophy" size={18} />} />
                <Tile label="Turns taken" value={(lifetime.turnsLeft || 0) + (lifetime.turnsRight || 0)} color={MUTED_BOX} icon={<Icon name="refresh" size={18} />} />
                <Tile label="Longest survival" value={lifetime.longestSurvival ? Math.round(lifetime.longestSurvival) + "s" : "–"} color={MUTED_BOX} icon={<Icon name="timer" size={18} />} />
                <Tile label="Time wormed" value={lifetime.aliveTime ? formatDuration(lifetime.aliveTime) : "–"} color={MUTED_BOX} icon={<Icon name="timer" size={18} />} hint="Total time alive across all rated rounds." />
              </div>
            </>
          ) : null}
        </>
      )}

      {/* Matches: sessions of arena cards, newest first (MatchesTab). The rating graph
          lives on the Overview. Every match here is a rated public match (that is
          all the server records). */}
      {shownTab === "matches" && (
        <MatchesTab
          history={history == null && rankedHistory == null ? null : [...(history ?? []), ...(rankedHistory ?? [])].sort((a, b) => a.createdAt.localeCompare(b.createdAt))}
          loading={loadingHistory || rankedHistory === undefined}
          accountId={accountId ?? null}
        />
      )}

      {/* No wrapping card here: the achievement tiles are boxes already, and a
          box around boxes read as clutter. Heading + count sit on the page. */}
      {shownTab === "achievements" && loadingAch && (
        <>
          <div className={styles.achHead}>
            <h3 className={styles.h}>Achievements</h3>
            <Skel w={90} h={10} />
          </div>
          <div className={styles.achGrid}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.ach}>
                <span className={styles.achIcon}><Skel w={26} h={26} round /></span>
                <div className={styles.achBody}>
                  <Skel w={i % 2 ? 110 : 140} h={12} />
                  <Skel w="85%" h={9} style={{ marginTop: 4 }} />
                  <div className={styles.achBar} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {shownTab === "achievements" && !loadingAch && (
        <>
          <div className={styles.achHead}>
            <h3 className={styles.h}>Achievements</h3>
            <span className={styles.achCount}>{levelsReached} / {levelsTotal} levels</span>
          </div>
          {!other && <OpenBanner unopened={unopened} />}
          {ACHIEVEMENT_GROUPS.map((g) => (
            <div key={g} className={styles.achGroup}>
              <h4 className={styles.achGroupName}>{g}</h4>
              <div className={styles.achGrid}>
                {achievements.filter((a) => a.group === g).map((a) => {
                  const color = levelColor(a.levels > 1 ? Math.max(1, a.level) : 3);
                  return (
                    <OpenableCard key={a.id} items={other ? [] : unopened.filter((u) => u.id === a.id)} className={`${styles.ach} ${a.level ? styles.achReached : ""} ${a.done ? styles.achDone : ""} ${!other && unopened.some((u) => u.id === a.id) ? styles.achWaiting : ""}`} style={{ "--lv": color } as React.CSSProperties}>
                      <span className={styles.achMedal} aria-hidden="true" data-medal="">
                        <span className={styles.achIcon}>{a.icon}</span>
                        {a.levels > 1 && a.level ? <b className={styles.achNumeral}>{LEVEL_NUMERALS[a.level - 1]}</b> : null}
                      </span>
                      <div className={styles.achBody}>
                        <span className={styles.achTop}><span className={styles.achName}>{a.name}</span><LevelHexes levels={a.levels} level={a.level} /></span>
                        <span className={styles.achDesc}>{(a.done ? (a.levels > 1 ? "Every level reached. " : "") : a.level && a.levels > 1 ? "Next: " : "") + sentence(a.goal)}</span>
                        <AchProgress a={a} />
                      </div>
                    </OpenableCard>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
